import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Phaser from 'phaser';
import socket from '../utils/socketClient';
import { isDeferring, registrarOnSaldoFinal } from '../utils/saldoControl';
import { useUser } from '../context/UserContext';
import { fetchUserData } from '../utils/api';

/**
 * Hook reutilizable para inicializar y manejar una instancia de Phaser.
 * Centraliza la carga de usuario, la creación del juego, el listener de socket
 * y el registro de callbacks de saldo.
 *
 * @param {object}  options
 * @param {Array}   options.scenes            - Escenas de Phaser a cargar
 * @param {number}  options.width             - Ancho del canvas
 * @param {number}  options.height            - Alto del canvas
 * @param {string}  [options.containerId]     - ID del div contenedor (alternativa al ref)
 * @param {boolean} [options.useSaldoDirecto] - Si true, registra dispararSaldoFinal para updates instantáneos de saldo
 */
const usePhaserGame = ({
  scenes,
  width,
  height,
  containerId = null,
  useSaldoDirecto = false,
}) => {
  const containerRef = useRef(null);
  const gameRef = useRef(null);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const { debitSaldo, setSaldoDirecto, silentRefreshUserData } = useUser();

  useEffect(() => {
    fetchUserData(navigate, (data) => setUserData(data));
  }, [navigate]);

  useEffect(() => {
    if (!userData) return;

    const config = {
      type: Phaser.AUTO,
      width,
      height,
      physics: { default: 'arcade', arcade: { gravity: { y: 200 } } },
      scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
      parent: containerId ?? containerRef.current,
      dom: { createContainer: true },
      scene: scenes,
      pauseOnBlur: false,
      disableVisibilityChange: true,
    };

    if (!gameRef.current) {
      gameRef.current = new Phaser.Game(config);
    }

    gameRef.current.saldoDisponible = userData.saldo;

    return () => {
      if (gameRef.current) gameRef.current.destroy(true);
    };
  }, [userData]);

  useEffect(() => {
    const handleSaldoUpdate = (data) => {
      if (userData && data.userId === userData.id) {
        if (isDeferring()) {
          gameRef.current.saldoPendiente = data.nuevoSaldo;
          return;
        }
        gameRef.current.saldoDisponible = data.nuevoSaldo;
      }
    };

    socket.on('actualizacion-saldo', handleSaldoUpdate);
    return () => socket.off('actualizacion-saldo', handleSaldoUpdate);
  }, [userData]);

  useEffect(() => {
    if (useSaldoDirecto) {
      registrarOnSaldoFinal((saldo) => setSaldoDirecto(saldo));
    }
    if (gameRef.current) {
      gameRef.current.onApuestaIniciada = (monto) => debitSaldo(monto);
      gameRef.current.onPartidaFinalizada = () => silentRefreshUserData();
    }
  }, [debitSaldo, setSaldoDirecto, silentRefreshUserData, userData, useSaldoDirecto]);

  return containerRef;
};

export default usePhaserGame;
